import { Injectable } from '@angular/core';

// Auth token service.
// SECURITY: The real authentication/authorization MUST be enforced server-side.
// This service only provides a client-side UX gate (show login form vs report).
// BE must validate the token on EVERY data API and return 401 when it is
// missing / invalid / expired. Never treat "a value exists in localStorage" as
// proof of login (that was the original auth-bypass bug).
const TOKEN_KEY = 'AGENT_TOKEN';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  setToken(token: string): void {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    }
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  clear(): void {
    localStorage.removeItem(TOKEN_KEY);
  }

  // Returns true only when a token is present and (if it is a JWT) not expired.
  // This is a UX gate only; BE remains the source of truth.
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    const exp = this.getJwtExp(token);
    if (exp === null) {
      // Not a JWT (or no exp claim) -> fall back to presence check.
      return true;
    }

    // exp is in seconds since epoch.
    const now = Math.floor(Date.now() / 1000);
    return exp > now;
  }

  // Decodes the `exp` claim from a JWT using a tiny inline base64url decode.
  // Returns null when the token is not a well-formed JWT or has no exp claim.
  private getJwtExp(token: string): number | null {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    try {
      let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      // Pad to a multiple of 4 for atob.
      while (payload.length % 4 !== 0) {
        payload += '=';
      }
      const json = JSON.parse(atob(payload));
      return typeof json.exp === 'number' ? json.exp : null;
    } catch {
      return null;
    }
  }
}
